import React from 'react';
import PropTypes from 'prop-types';
import { faPalette } from '@fortawesome/free-solid-svg-icons/faPalette';
import { faShare } from '@fortawesome/free-solid-svg-icons/faShare';
import { Accordion, Tabs } from '@appcues/sonar';
import { EditorShape } from 'entities/step-groups/shape';
import { ContentShape } from 'entities/step-children/shape';
import { STANDARD, SLIDEOUT } from 'lib/presentation/presentation';
import ActionsShape from 'lib/actions/shape';
import { hasSurveyBlockType } from 'lib/block/survey-handler';
import { COLOR_MODES } from 'lib/user-preferences/user-preferences';
import { MODAL } from 'lib/trait/trait';
import { Content } from 'components/SideBarSettings/Shared/styled';
import AlignmentSpacing from 'components/SideBarSettings/Shared/AlignmentSpacing';
import Backdrop from 'components/SideBarSettings/Shared/Backdrop';
import Container from 'components/SideBarSettings/Shared/Container';
import DropShadow from 'components/SideBarSettings/Shared/DropShadow';
import Effects from 'components/SideBarSettings/Shared/Effects';
import Navigation from 'components/SideBarSettings/Shared/Navigation';
import ThemeVariantSelect from 'components/SideBarSettings/Shared/ThemeVariantSelect';
import Details from './Details';
import Position from './Position';

export function ModalSettings({
  selectedStepGroup,
  isStepGroupFirstChild,
  editor,
  content,
  stepChildrenIds = [],
  actions,
  colorMode,
  handleActionsUpdate,
  onChange,
}) {
  const { presentation, style } = editor;
  const isSingleStep = stepChildrenIds.length === 1;
  const hasSurveyBlock = hasSurveyBlockType(content);

  const onThemeChange = patch => {
    // Special case to preserve slideout structural styles
    if (patch.presentation === SLIDEOUT) {
      onChange({
        ...patch,
        style: {
          ...patch.style,
          horizontalAlignment: editor.style?.horizontalAlignment,
          verticalAlignment: editor.style?.verticalAlignment,
          width: editor.style?.width,
        },
      });
      return;
    }

    onChange(patch);
  };

  const StyleContent = (
    <Accordion.Root
      collapsible="true"
      defaultValue={['details', 'container']}
      type="multiple"
    >
      <ThemeVariantSelect
        type={MODAL}
        editor={editor}
        onChange={onThemeChange}
      />

      <Details
        editor={editor}
        isSingleStep={isSingleStep}
        hasSurveyBlock={hasSurveyBlock}
        onChange={onChange}
      />

      <Container editor={editor} colorMode={colorMode} onChange={onChange} />

      {presentation === SLIDEOUT && (
        <Position editor={editor} onChange={onChange} />
      )}

      {[STANDARD, SLIDEOUT].includes(presentation) && (
        <>
          <DropShadow
            editor={editor}
            colorMode={colorMode}
            onChange={onChange}
          />

          <Backdrop editor={editor} colorMode={colorMode} onChange={onChange} />

          <Effects
            key={selectedStepGroup}
            editor={editor}
            stepGroup={selectedStepGroup}
            onChange={onChange}
          />
        </>
      )}

      <AlignmentSpacing
        style={style}
        spacingLabel="Content"
        onChange={onChange}
        hasPadding
        hasMargin={presentation === SLIDEOUT}
      />
    </Accordion.Root>
  );

  const ModalTabs = (
    <Tabs.Root defaultValue="style" align="stretch">
      <Tabs.List>
        <Tabs.Trigger value="style" icon={faPalette}>
          Style
        </Tabs.Trigger>
        <Tabs.Trigger value="navigation" icon={faShare}>
          Navigation
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="style">{StyleContent}</Tabs.Content>
      <Tabs.Content value="navigation">
        <Navigation
          stepGroup={selectedStepGroup}
          actions={actions}
          handleActionsUpdate={handleActionsUpdate}
        />
      </Tabs.Content>
    </Tabs.Root>
  );

  return <Content>{isStepGroupFirstChild ? ModalTabs : StyleContent}</Content>;
}

ModalSettings.propTypes = {
  selectedStepGroup: PropTypes.string,
  isStepGroupFirstChild: PropTypes.bool,
  editor: EditorShape,
  content: ContentShape,
  stepChildrenIds: PropTypes.arrayOf(PropTypes.string),
  actions: ActionsShape,
  colorMode: PropTypes.oneOf(COLOR_MODES),
  onChange: PropTypes.func,
  handleActionsUpdate: PropTypes.func,
};

export default ModalSettings;
